<script lang="ts">
  import { m } from "#messages";
  import Avatar from "#lib/ui/Avatar.svelte";
  import IconButton from "#lib/ui/IconButton.svelte";
  import DeferredMenu from "#lib/ui/DeferredMenu.svelte";
  import type { MenuItem } from "#lib/ui/Menu.svelte";
  import type ManageAccounts from "./ManageAccounts.svelte";
  import { addAccountHref, heldAccounts, signOutOf, switchTo } from "./accounts.ts";
  import type { HeldAccount, HeldAccounts } from "./accounts.ts";
  import type { SessionUser } from "./me.ts";

  let { me }: { me: SessionUser } = $props();

  let held = $state.raw<HeldAccounts | null>(null);
  let reading: Promise<void> | undefined;
  let leaving = $state(false);
  let manage = $state.raw<typeof ManageAccounts | null>(null);
  let managing = $state(false);

  const refresh = (): Promise<void> =>
    (reading = heldAccounts().then((found) => {
      held = found;
      if (found === null) reading = undefined;
    }));

  const read = (): Promise<void> => reading ?? refresh();

  const signedIn = $derived<readonly HeldAccount[]>(
    held === null
      ? [
          {
            accountId: me.accountId,
            discordUserId: me.discordUserId,
            username: me.username,
            avatarUrl: me.avatarUrl,
            active: true,
          },
        ]
      : held.accounts,
  );

  const roomForMore = $derived(held === null || held.accounts.length < held.limit);

  const leave = (run: () => Promise<boolean>): void => {
    leaving = true;
    void run().then((done) => {
      if (done) return;
      leaving = false;
      void refresh();
    });
  };

  const openManage = (): void => {
    void Promise.all([read(), import("./ManageAccounts.svelte")]).then(([, loaded]) => {
      manage = loaded.default;
      managing = true;
    });
  };

  const items = $derived<readonly MenuItem[]>([
    ...signedIn.map((account): MenuItem => ({
      label: account.username,
      avatar: { src: account.avatarUrl, name: account.username },
      checked: account.active,
      disabled: leaving,
      stay: !account.active,
      testId: account.active ? "menu-account-active" : "menu-account",
      ...(account.active ? {} : { onselect: () => leave(() => switchTo(account.accountId)) }),
    })),
    ...(roomForMore
      ? [
          {
            label: m.menu_add_account(),
            icon: "add",
            stay: true,
            testId: "menu-add-account",
            onselect: () => globalThis.location.assign(addAccountHref(globalThis.location.origin)),
          } as const,
        ]
      : []),
    "separator",
    ...(me.staff
      ? [{ label: m.menu_platform_admin(), href: "/admin", testId: "menu-platform-admin" }]
      : []),
    { label: m.menu_manage_accounts(), testId: "menu-manage-accounts", onselect: openManage },
    {
      label: m.menu_signout(),
      onselect: () => leave(() => signOutOf({ accountId: me.accountId, active: true })),
      danger: true,
      disabled: leaving,
      stay: true,
      testId: "menu-signout",
    },
  ]);
</script>

<span class="contents" role="presentation" onpointerenter={read} onfocusin={read}>
  <DeferredMenu label={m.avatar_menu()} {items} align="end" width="lg">
    {#snippet trigger(props)}
      <IconButton label={m.avatar_menu()} {...props}>
        <Avatar src={me.avatarUrl} name={me.username} size={22} />
      </IconButton>
    {/snippet}
  </DeferredMenu>
</span>

{#if manage !== null}
  {@const Manage = manage}
  <Manage bind:open={managing} accounts={signedIn} {roomForMore} onchange={refresh} />
{/if}
